import React, { useState, useEffect } from "react";
import axios from "axios";
import { xml2json } from "xml-js";
import * as mammoth from "mammoth/mammoth.browser";

const UploadRubric = ({ platformUrl }) => {
    const [sourceType, setSourceType] = useState("d2l");
    const [rawJsonData, setRawJsonData] = useState(null);
    const [jsonData, setJsonData] = useState([]);
    const [selectedRubricIds, setSelectedRubricIds] = useState([]);
    const [courseId, setCourseId] = useState("");
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [uploadResults, setUploadResults] = useState([]);
    const [goalXmlData, setGoalXmlData] = useState(null);
    const [goalDownloadFileName, setGoalDownloadFileName] = useState("blackboard-goals.xml");
    const [goalSetTitle, setGoalSetTitle] = useState("");

    const params = new URLSearchParams(window.location.search);
    console.log("params:" + JSON.stringify(window.location.search));

    const token = params.get("token");
    const courseUuid = params.get("courseUuid");
    const isLearningOutcomeSource = sourceType === "canvas_outcomes";

    useEffect(() => {
        const fetchCourseDetails = async () => {
            if (!courseUuid || courseUuid.includes("_1")) return;

            try {
                const response = await axios.get(
                    `${platformUrl}/learn/api/public/v1/courses/uuid:${courseUuid}`,
                    {
                        headers: { Authorization: `Bearer ${token}` }
                    }
                );
                const fetchedCourseId = response.data.courseId;
                setCourseId(fetchedCourseId);
                setMessage(`Course ID loaded: ${fetchedCourseId}`);
            } catch (err) {
                setError(`Failed to fetch course details: ${err.message}`);
            }
        };

        fetchCourseDetails();
    }, [courseUuid, platformUrl, token]);

    const cleanHtml = (text) => {
        return text
            ? String(text)
                .replace(/<[^>]*>/g, "")
                .replace(/&nbsp;/g, " ")
                .replace(/\s+/g, " ")
                .trim()
            : "";
    };

    const asArray = (value) => {
        if (!value) return [];
        return Array.isArray(value) ? value : [value];
    };

    const getNodeText = (node) => {
        if (!node) return "";

        if (typeof node === "string" || typeof node === "number") return String(node);

        if (Array.isArray(node)) {
            return node.map(getNodeText).filter(Boolean).join(" ").replace(/\s+/g, " ").trim();
        }

        if (typeof node._text === "string" || typeof node._text === "number") {
            return String(node._text);
        }

        if (typeof node._cdata === "string" || typeof node._cdata === "number") {
            return String(node._cdata);
        }

        if (node.text) {
            const text = getNodeText(node.text);
            if (text) return text;
        }

        return Object.entries(node)
            .filter(([key]) => key !== "_attributes")
            .map(([, value]) => getNodeText(value))
            .filter(Boolean)
            .join(" ")
            .replace(/\s+/g, " ")
            .trim();
    };

    const splitCanvasRatingDescription = (text) => {
        const clean = cleanHtml(text || "").trim();
        const idx = clean.indexOf(":");

        if (idx === -1) {
            return {
                title: clean,
                detail: clean
            };
        }

        return {
            title: clean.slice(0, idx).trim(),
            detail: clean.slice(idx + 1).trim()
        };
    };

    const buildCanvasColumnKey = (points) => {
        const n = Number(points || 0);
        return Number.isFinite(n) ? n.toFixed(6) : "0.000000";
    };

    const buildCsvColumnKey = (header, points, position) => {
        const safeHeader = String(header || "").trim();
        const n = Number(points || 0);
        const safePoints = Number.isFinite(n) ? n.toFixed(6) : "0.000000";
        const safePos = Number.isFinite(Number(position)) ? String(Number(position)) : "0";
        return `${safeHeader}__${safePoints}__${safePos}`;
    };

    const buildCsvRowKey = (header, points, position) => {
        const safeHeader = String(header || "").trim();
        const n = Number(points || 0);
        const safePoints = Number.isFinite(n) ? n.toFixed(6) : "0.000000";
        const safePos = Number.isFinite(Number(position)) ? String(Number(position)) : "0";
        return `${safeHeader}__${safePoints}__${safePos}`;
    };

    const getFileBaseName = (fileName) => {
        return String(fileName || "Word Rubric")
            .replace(/\.[^/.]+$/, "")
            .trim() || "Word Rubric";
    };

    const escapeXml = (value) => {
        return String(value ?? "")
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&apos;");
    };

    const sanitizeXmlId = (value, fallback = "ID") => {
        const safe = String(value || fallback)
            .replace(/<[^>]*>/g, "")
            .replace(/[^A-Za-z0-9_-]/g, "_")
            .replace(/_+/g, "_")
            .replace(/^_+|_+$/g, "")
            .slice(0, 37);

        return safe || fallback;
    };

    const normalizeGoalSubjectCode = (value) => {
        return sanitizeXmlId(value, "GOALS").slice(0, 5).toUpperCase() || "GOALS";
    };

    const getCurrentRevisionDate = () => {
        const now = new Date();
        const pad = (n) => String(n).padStart(2, "0");
        return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
    };

    const getLearningOutcomeDescription = (outcome) => {
        return cleanHtml(getNodeText(outcome?.description)) || cleanHtml(getNodeText(outcome?.title));
    };

    const parseCanvasLearningOutcomesFile = (parsed, fileName) => {
        const root = parsed?.learningOutcomes || parsed?.outcomes || parsed;
        const outcomeNodes = asArray(root?.learningOutcome || root?.learning_outcome);

        if (!outcomeNodes.length) {
            throw new Error("Invalid learning outcomes XML: expected <learningOutcomes><learningOutcome>.");
        }

        const baseName = getFileBaseName(fileName || "Learning Outcomes");

        return {
            sourceType: "canvas_outcomes",
            fileName,
            title: baseName,
            outcomes: outcomeNodes.map((outcome, index) => {
                const identifier =
                    outcome?._attributes?.identifier ||
                    getNodeText(outcome?.identifier) ||
                    getNodeText(outcome?.external_identifier) ||
                    `LO_${index + 1}`;

                const title = cleanHtml(getNodeText(outcome?.title)) || `Learning Outcome ${index + 1}`;
                const description = getLearningOutcomeDescription(outcome);
                const externalIdentifier = cleanHtml(getNodeText(outcome?.external_identifier));

                return {
                    identifier,
                    title,
                    description: description || title,
                    externalIdentifier,
                    position: index + 1
                };
            })
        };
    };

    const buildBlackboardGoalsXmlFromOutcomes = (outcomes, options = {}) => {
        const safeCourseId = sanitizeXmlId(options.courseId || courseId || "COURSE", "COURSE");
        const setName = cleanHtml(options.goalSetTitle || goalSetTitle || `${safeCourseId} Learning Outcomes`);
        const documentUid = sanitizeXmlId(`${safeCourseId}_LO`, "COURSE_LO");
        const subjectCode = normalizeGoalSubjectCode(safeCourseId);
        const documentSetUid = sanitizeXmlId(`${safeCourseId}_OUTCOMES`, "OUTCOMES");
        const categoryCode = sanitizeXmlId(`${safeCourseId}_CAT`, "LO_CAT");
        const standardUid = sanitizeXmlId(`${safeCourseId}_STANDARD`, "LO_STANDARD");
        const revisionDate = getCurrentRevisionDate();
        const year = String(new Date().getFullYear());

        const items = outcomes.map((outcome, index) => {
            const sequence = String(index + 1).padStart(3, "0");
            const sourceId = outcome.externalIdentifier || outcome.identifier || sequence;
            const itemUid = sanitizeXmlId(`${safeCourseId}_LO_${sourceId}_${sequence}`, `LO_${sequence}`);
            const docNum = sanitizeXmlId(`${safeCourseId}_LO_${sequence}`, `LO_${sequence}`);
            const statement = outcome.description || outcome.title || `Learning Outcome ${index + 1}`;

            return `      <item uid="${escapeXml(itemUid)}" parent_uid="" label="Learning Outcome" linkable="Y" stem="The student will:" doc_num="${escapeXml(docNum)}">
         <statement>${escapeXml(statement)}</statement>
      </item>`;
        }).join("\n");

        return `<?xml version="1.0" encoding="UTF-8"?>
<standard_document year="${escapeXml(year)}" revision_date="${escapeXml(revisionDate)}" uid="${escapeXml(documentUid)}" uid_provider="LO" status="A" editable="true">
   <organization org_code="${escapeXml(safeCourseId)}" country_code="US">${escapeXml(options.organizationName || "Imported Learning Outcomes")}</organization>
   <subject code="${escapeXml(subjectCode)}">${escapeXml(setName)}</subject>
   <document_set uid="${escapeXml(documentSetUid)}">Course Learning Outcomes</document_set>
   <grade_range lo="1" hi="1" code="${escapeXml(categoryCode)}">Learning Outcomes</grade_range>
   <standard uid="${escapeXml(standardUid)}">
      <title>Learning Outcomes</title>
      <grade_range_ref code="${escapeXml(categoryCode)}"/>
${items}
   </standard>
</standard_document>`;
    };

    const downloadTextFile = (content, fileName, mimeType) => {
        const blob = new Blob([content], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const parsePointsFromHeader = (header, fallbackPoints) => {
        const text = String(header || "");

        const percentMatch = text.match(/(-?\d+(?:\.\d+)?)\s*%/);
        if (percentMatch) {
            const n = parseFloat(percentMatch[1]);
            if (Number.isFinite(n)) return n;
        }

        const numberMatch = text.match(/(-?\d+(?:\.\d+)?)/);
        if (numberMatch) {
            const n = parseFloat(numberMatch[1]);
            if (Number.isFinite(n)) return n;
        }

        return fallbackPoints;
    };

    const normalizeWordCellText = (cell) => {
        return cleanHtml(cell?.textContent || "");
    };

    const parseWordTableRows = (table) => {
        return Array.from(table.querySelectorAll("tr"))
            .map((tr) =>
                Array.from(tr.querySelectorAll("th,td"))
                    .map(normalizeWordCellText)
            )
            .filter((row) => row.some((cell) => cell.trim() !== ""));
    };

    const parseWordDocxFile = async (file) => {
        const arrayBuffer = await file.arrayBuffer();

        const result = await mammoth.convertToHtml({
            arrayBuffer
        });

        const parser = new DOMParser();
        const doc = parser.parseFromString(result.value || "", "text/html");
        const tables = Array.from(doc.querySelectorAll("table"));

        if (!tables.length) {
            throw new Error(
                "No tables were found in the Word document. The Word rubric should be formatted as a table."
            );
        }

        const rubrics = [];
        const baseTitle = getFileBaseName(file.name);

        tables.forEach((table, tableIndex) => {
            const rows = parseWordTableRows(table);

            if (rows.length < 2) {
                return;
            }

            const headerRowIndex = rows.findIndex((row) => {
                const firstCell = String(row[0] || "").toLowerCase();
                const hasCriteria = firstCell.includes("criteria") || firstCell.includes("criterion");
                const hasRatingColumns = row.slice(1).some((cell) => String(cell || "").trim() !== "");
                return hasCriteria && hasRatingColumns;
            });

            const effectiveHeaderRowIndex = headerRowIndex >= 0 ? headerRowIndex : 0;
            const headerRow = rows[effectiveHeaderRowIndex];

            if (!headerRow || headerRow.length < 2) {
                return;
            }

            const ratingHeaders = headerRow
                .slice(1)
                .map((header) => cleanHtml(header))
                .filter((header) => header !== "");

            if (!ratingHeaders.length) {
                return;
            }

            const fallbackMax = ratingHeaders.length;
            const columns = ratingHeaders.map((header, index) => {
                const fallbackPoints = fallbackMax - index;
                const points = parsePointsFromHeader(header, fallbackPoints);

                return {
                    id: `word_rubric_${tableIndex}_col_${index}`,
                    header,
                    position: index,
                    points
                };
            });

            const maxPoints = Math.max(...columns.map((column) => column.points), 0);

            const criteriaRows = rows
                .slice(effectiveHeaderRowIndex + 1)
                .filter((row) => {
                    const criterion = cleanHtml(row[0] || "");
                    return criterion !== "" && row.slice(1).some((cell) => cleanHtml(cell) !== "");
                });

            if (!criteriaRows.length) {
                return;
            }

            const rubricTitle =
                tables.length === 1 ? baseTitle : `${baseTitle} - Table ${tableIndex + 1}`;

            rubrics.push({
                title: rubricTitle,
                rubricType: "Numeric",
                columns,
                criteriaRows: criteriaRows.map((row, rowIndex) => ({
                    id: `word_rubric_${tableIndex}_row_${rowIndex}`,
                    header: cleanHtml(row[0] || `Criterion ${rowIndex + 1}`),
                    position: rowIndex,
                    rowPoints: maxPoints,
                    descriptions: columns.map((column, columnIndex) =>
                        cleanHtml(row[columnIndex + 1] || "")
                    )
                }))
            });
        });

        if (!rubrics.length) {
            throw new Error(
                "No valid rubric tables were found. Expected the first row to contain Criteria plus rating columns."
            );
        }

        return {
            sourceType: "word",
            rubrics
        };
    };

    const parseD2LFile = (parsed) => {
        let rubrics = [];

        if (parsed?.rubric) {
            rubrics = [parsed.rubric];
        } else if (parsed?.rubrics?.rubric) {
            rubrics = asArray(parsed.rubrics.rubric);
        } else {
            throw new Error("Invalid D2L XML structure: expected <rubric> or <rubrics><rubric>");
        }

        return {
            sourceType: "d2l",
            rubrics
        };
    };

    const parseCanvasFile = (parsed) => {
        const root = parsed?.rubrics;
        const rubricNodes = asArray(root?.rubric);

        if (!rubricNodes.length) {
            throw new Error("Invalid Canvas XML structure: expected <rubrics><rubric>");
        }

        return {
            sourceType: "canvas",
            rubrics: rubricNodes
        };
    };

    const parseTurnitinRbcFile = (parsed, fileName) => {
        const rubric = parsed?.Rubric || parsed?.rubric || {};
        const scales = asArray(parsed?.RubricScale || parsed?.rubricScale || parsed?.scales);
        const criteria = asArray(parsed?.RubricCriterion || parsed?.rubricCriterion || parsed?.criteria);
        const criterionScales = asArray(
            parsed?.RubricCriterionScale ||
            parsed?.rubricCriterionScale ||
            parsed?.criterionScales ||
            parsed?.cells
        );

        if (!scales.length) {
            throw new Error("Invalid Turnitin RBC structure: expected RubricScale array.");
        }

        if (!criteria.length) {
            throw new Error("Invalid Turnitin RBC structure: expected RubricCriterion array.");
        }

        return {
            sourceType: "turnitin",
            fileName,
            rubrics: [
                {
                    title:
                        cleanHtml(rubric?.name || rubric?.title) ||
                        getFileBaseName(fileName || "Turnitin Rubric"),
                    description: cleanHtml(rubric?.description || ""),
                    rubricType: rubric?.rubric_type || rubric?.type || "standard",
                    scales,
                    criteria,
                    criterionScales
                }
            ]
        };
    };

    const parseCsvLine = (line) => {
        const result = [];
        let current = "";
        let inQuotes = false;

        for (let i = 0; i < line.length; i++) {
            const ch = line[i];
            const next = line[i + 1];

            if (ch === '"') {
                if (inQuotes && next === '"') {
                    current += '"';
                    i++;
                } else {
                    inQuotes = !inQuotes;
                }
            } else if (ch === "," && !inQuotes) {
                result.push(current);
                current = "";
            } else {
                current += ch;
            }
        }

        result.push(current);
        return result.map((v) => v.trim());
    };

    const parseCsvText = (csvText) => {
        const lines = String(csvText || "")
            .replace(/\r\n/g, "\n")
            .replace(/\r/g, "\n")
            .split("\n")
            .filter((line) => line.trim() !== "");

        if (!lines.length) {
            throw new Error("CSV file is empty");
        }

        const headers = parseCsvLine(lines[0]).map((h) => h.trim().toLowerCase());

        const requiredHeaders = [
            "rubric_title",
            "rubric_type",
            "row_header",
            "row_points",
            "row_position",
            "column_header",
            "column_points",
            "column_position",
            "cell_description"
        ];

        const missing = requiredHeaders.filter((h) => !headers.includes(h));
        if (missing.length) {
            throw new Error(`CSV is missing required column(s): ${missing.join(", ")}`);
        }

        const rows = lines.slice(1).map((line, index) => {
            const values = parseCsvLine(line);
            const rowObj = {};

            headers.forEach((header, i) => {
                rowObj[header] = values[i] !== undefined ? values[i] : "";
            });

            rowObj.__lineNumber = index + 2;
            return rowObj;
        });

        if (!rows.length) {
            throw new Error("CSV file has headers but no data rows");
        }

        return {
            sourceType: "csv",
            rubrics: rows
        };
    };

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setMessage("");
        setError("");
        setRawJsonData(null);
        setJsonData([]);
        setSelectedRubricIds([]);
        setUploadResults([]);
        setGoalXmlData(null);

        try {
            let normalized;

            if (sourceType === "word") {
                normalized = await parseWordDocxFile(file);
            } else {
                const fileText = await file.text();

                if (sourceType === "csv") {
                    normalized = parseCsvText(fileText);
                } else if (sourceType === "turnitin") {
                    const parsed = JSON.parse(fileText);
                    normalized = parseTurnitinRbcFile(parsed, file.name);
                } else {
                    const parsed = JSON.parse(xml2json(fileText, { compact: true }));
                    normalized =
                        sourceType === "canvas_outcomes"
                            ? parseCanvasLearningOutcomesFile(parsed, file.name)
                            : sourceType === "canvas"
                                ? parseCanvasFile(parsed)
                                : parseD2LFile(parsed);
                }
            }

            setRawJsonData(normalized);
            setJsonData([]);
            setSelectedRubricIds([]);
            setMessage(
                `File successfully loaded. Found ${
                    normalized.sourceType === "csv"
                        ? normalized.rubrics.length + " CSV row(s)"
                        : normalized.sourceType === "canvas_outcomes"
                            ? normalized.outcomes.length + " learning outcome(s)"
                            : normalized.rubrics.length + " rubric(s)"
                } from ${sourceType === "canvas_outcomes" ? "CANVAS OUTCOMES" : sourceType.toUpperCase()}.`
            );
            setError("");
            setUploadResults([]);
        } catch (err) {
            setError(
                sourceType === "csv"
                    ? "Error parsing CSV file: " + err.message
                    : sourceType === "word"
                        ? "Error parsing Word document: " + err.message
                        : sourceType === "turnitin"
                            ? "Error parsing Turnitin RBC file: " + err.message
                            : "Error parsing XML file: " + err.message
            );
            setRawJsonData(null);
            setJsonData([]);
            setSelectedRubricIds([]);
        }
    };

    const transformD2LRubrics = (rubrics) => {
        return rubrics.map((rubric, rubricIndex) => {
            if (!rubric?._attributes) {
                throw new Error("Invalid D2L rubric structure");
            }

            const criteriaGroup = Array.isArray(rubric.criteria_groups?.criteria_group)
                ? rubric.criteria_groups.criteria_group[0]
                : rubric.criteria_groups?.criteria_group;

            if (!criteriaGroup) {
                throw new Error(`Rubric "${rubric?._attributes?.name || "Untitled"}" is missing criteria_group`);
            }

            const levels = asArray(criteriaGroup.level_set?.levels?.level);
            if (!levels.length) {
                throw new Error(`Rubric "${rubric?._attributes?.name || "Untitled"}" has no levels`);
            }

            const criteria = asArray(criteriaGroup.criteria?.criterion);
            if (!criteria.length) {
                throw new Error(`Rubric "${rubric?._attributes?.name || "Untitled"}" has no criteria`);
            }

            const columnPoints = {};

            let columns = levels
                .map((level) => {
                    const levelId = level?._attributes?.level_id || "";
                    const levelPoints = parseFloat(level?._attributes?.level_value || "0");
                    columnPoints[levelId] = levelPoints;

                    return {
                        id: levelId,
                        header: level?._attributes?.name || "",
                        position: parseInt(level?._attributes?.sort_order || "0", 10) - 1
                    };
                })
                .sort((a, b) => a.position - b.position);

            columns = columns.map((col, index) => ({ ...col, position: index }));

            let rows = criteria
                .map((criterion) => ({
                    id: criterion?._attributes?.sort_order || "",
                    header: criterion?._attributes?.name || "",
                    position: parseInt(criterion?._attributes?.sort_order || "0", 10) - 1,
                    rowPoints: Math.max(
                        ...levels.map((l) => parseFloat(l?._attributes?.level_value || "0")),
                        0
                    )
                }))
                .sort((a, b) => a.position - b.position);

            rows = rows.map((row, index) => ({ ...row, position: index }));

            const cells = [];

            rows.forEach((row) => {
                columns.forEach((column) => {
                    const matchingCriterion = criteria.find(
                        (c) => String(c?._attributes?.sort_order || "") === String(row.id)
                    );

                    const criterionCells = asArray(matchingCriterion?.cells?.cell);

                    const matchingCell = criterionCells.find(
                        (cell) => String(cell?._attributes?.level_id || "") === String(column.id)
                    );

                    const descriptionText = getNodeText(matchingCell?.description);
                    const cleanDescription = cleanHtml(descriptionText);

                    const rawCellValue = matchingCell?._attributes?.cell_value;
                    const effectivePoints =
                        rawCellValue !== undefined &&
                        rawCellValue !== null &&
                        String(rawCellValue).trim() !== ""
                            ? parseFloat(rawCellValue)
                            : columnPoints[column.id] || 0;

                    cells.push({
                        id: `${column.id}_${row.id}`,
                        rowId: row.id,
                        columnId: column.id,
                        rowPosition: row.position,
                        columnPosition: column.position,
                        points: { value: effectivePoints },
                        description: cleanDescription
                    });
                });
            });

            return {
                __localId: `rubric_${rubricIndex}`,
                title: rubric?._attributes?.name || "Untitled Rubric",
                rubricType: "Numeric",
                columns,
                rows,
                cells
            };
        });
    };

    const transformCanvasRubrics = (rubrics) => {
        return rubrics.map((rubric, rubricIndex) => {
            const title =
                getNodeText(rubric?.title) ||
                rubric?._attributes?.identifier ||
                `Untitled Rubric ${rubricIndex + 1}`;

            const criteria = asArray(rubric?.criteria?.criterion);

            if (!criteria.length) {
                throw new Error(`Canvas rubric "${title}" has no criteria`);
            }

            const columnMap = new Map();

            criteria.forEach((criterion) => {
                const ratings = asArray(criterion?.ratings?.rating);

                ratings.forEach((rating) => {
                    const ratingPoints = parseFloat(getNodeText(rating?.points) || "0");
                    const columnKey = buildCanvasColumnKey(ratingPoints);
                    const parsedDesc = splitCanvasRatingDescription(getNodeText(rating?.description));

                    if (!columnMap.has(columnKey)) {
                        columnMap.set(columnKey, {
                            id: `rubric_${rubricIndex}_col_${columnKey}`,
                            key: columnKey,
                            points: ratingPoints,
                            header: parsedDesc.title || `${ratingPoints}`
                        });
                    }
                });
            });

            const orderedColumnDefs = Array.from(columnMap.values()).sort((a, b) => b.points - a.points);

            const columns = orderedColumnDefs.map((column, index) => ({
                id: column.id,
                header: column.header,
                position: index
            }));

            const rows = criteria.map((criterion, index) => {
                const criterionPoints = parseFloat(getNodeText(criterion?.points) || "0");
                const description = getNodeText(criterion?.description);
                const longDescription = getNodeText(criterion?.long_description);
                const header = cleanHtml(longDescription || description || `Criterion ${index + 1}`);
                const criterionId = getNodeText(criterion?.criterion_id) || String(index + 1);

                return {
                    id: `rubric_${rubricIndex}_criterion_${criterionId}`,
                    header,
                    position: index,
                    rowPoints: criterionPoints
                };
            });

            const cells = [];

            rows.forEach((row, rowIndex) => {
                const criterion = criteria[rowIndex];
                const ratings = asArray(criterion?.ratings?.rating);

                const ratingByPointKey = new Map();
                ratings.forEach((rating) => {
                    const ratingPoints = parseFloat(getNodeText(rating?.points) || "0");
                    ratingByPointKey.set(buildCanvasColumnKey(ratingPoints), rating);
                });

                orderedColumnDefs.forEach((column, columnIndex) => {
                    const matchingRating = ratingByPointKey.get(column.key);
                    const parsedDesc = splitCanvasRatingDescription(
                        getNodeText(matchingRating?.description)
                    );

                    cells.push({
                        id: `${column.id}_${row.id}`,
                        rowId: row.id,
                        columnId: column.id,
                        rowPosition: row.position,
                        columnPosition: columnIndex,
                        points: { value: column.points },
                        description: parsedDesc.detail || ""
                    });
                });
            });

            return {
                __localId: `rubric_${rubricIndex}`,
                title,
                rubricType: "Numeric",
                columns,
                rows,
                cells
            };
        });
    };

    const transformCsvRubrics = (csvRows) => {
        const grouped = new Map();

        csvRows.forEach((row, index) => {
            const rubricTitle = String(row.rubric_title || "").trim();
            const rubricType = String(row.rubric_type || "Numeric").trim() || "Numeric";
            const rowHeader = String(row.row_header || "").trim();
            const columnHeader = String(row.column_header || "").trim();
            const rowPoints = parseFloat(row.row_points || "0");
            const columnPoints = parseFloat(row.column_points || "0");
            const rowPosition = parseInt(row.row_position, 10);
            const columnPosition = parseInt(row.column_position, 10);
            const cellDescription = cleanHtml(row.cell_description || "");

            if (!rubricTitle) {
                throw new Error(`CSV row ${row.__lineNumber || index + 2} is missing rubric_title`);
            }
            if (!rowHeader) {
                throw new Error(`CSV row ${row.__lineNumber || index + 2} is missing row_header`);
            }
            if (!columnHeader) {
                throw new Error(`CSV row ${row.__lineNumber || index + 2} is missing column_header`);
            }
            if (!Number.isFinite(rowPoints)) {
                throw new Error(`CSV row ${row.__lineNumber || index + 2} has invalid row_points`);
            }
            if (!Number.isFinite(columnPoints)) {
                throw new Error(`CSV row ${row.__lineNumber || index + 2} has invalid column_points`);
            }
            if (!Number.isInteger(rowPosition)) {
                throw new Error(`CSV row ${row.__lineNumber || index + 2} has invalid row_position`);
            }
            if (!Number.isInteger(columnPosition)) {
                throw new Error(`CSV row ${row.__lineNumber || index + 2} has invalid column_position`);
            }

            if (!grouped.has(rubricTitle)) {
                grouped.set(rubricTitle, {
                    title: rubricTitle,
                    rubricType,
                    rows: [],
                    columns: [],
                    cells: [],
                    rowMap: new Map(),
                    columnMap: new Map()
                });
            }

            const rubric = grouped.get(rubricTitle);

            const rowKey = buildCsvRowKey(rowHeader, rowPoints, rowPosition);
            const columnKey = buildCsvColumnKey(columnHeader, columnPoints, columnPosition);

            if (!rubric.rowMap.has(rowKey)) {
                const rowId = `rubric_${grouped.size - 1}_row_${rubric.rowMap.size}`;
                rubric.rowMap.set(rowKey, {
                    id: rowId,
                    header: rowHeader,
                    position: rowPosition,
                    rowPoints
                });
                rubric.rows.push(rubric.rowMap.get(rowKey));
            } else {
                const existingRow = rubric.rowMap.get(rowKey);
                if (existingRow.position !== rowPosition) {
                    throw new Error(
                        `Conflicting row_position for rubric "${rubricTitle}", row "${rowHeader}"`
                    );
                }
            }

            if (!rubric.columnMap.has(columnKey)) {
                const columnId = `rubric_${grouped.size - 1}_col_${rubric.columnMap.size}`;
                rubric.columnMap.set(columnKey, {
                    id: columnId,
                    header: columnHeader,
                    position: columnPosition,
                    points: columnPoints
                });
                rubric.columns.push(rubric.columnMap.get(columnKey));
            } else {
                const existingColumn = rubric.columnMap.get(columnKey);
                if (existingColumn.position !== columnPosition) {
                    throw new Error(
                        `Conflicting column_position for rubric "${rubricTitle}", column "${columnHeader}"`
                    );
                }
            }

            const rowDef = rubric.rowMap.get(rowKey);
            const columnDef = rubric.columnMap.get(columnKey);

            const duplicate = rubric.cells.find(
                (cell) => cell.rowId === rowDef.id && cell.columnId === columnDef.id
            );

            if (duplicate) {
                throw new Error(
                    `Duplicate CSV cell found for rubric "${rubricTitle}", row "${rowHeader}", column "${columnHeader}"`
                );
            }

            rubric.cells.push({
                id: `${columnDef.id}_${rowDef.id}`,
                rowId: rowDef.id,
                columnId: columnDef.id,
                rowPosition: rowDef.position,
                columnPosition: columnDef.position,
                points: { value: columnDef.points },
                description: cellDescription
            });
        });

        return Array.from(grouped.values()).map((rubric, rubricIndex) => {
            const sortedRows = [...rubric.rows].sort((a, b) => a.position - b.position);
            const sortedColumns = [...rubric.columns].sort((a, b) => a.position - b.position);

            const rowPositionMap = new Map();
            const columnPositionMap = new Map();

            sortedRows.forEach((row, idx) => {
                rowPositionMap.set(row.id, idx);
            });

            sortedColumns.forEach((col, idx) => {
                columnPositionMap.set(col.id, idx);
            });

            return {
                __localId: `rubric_${rubricIndex}`,
                title: rubric.title,
                rubricType: rubric.rubricType || "Numeric",
                columns: sortedColumns.map((col, idx) => ({
                    id: col.id,
                    header: col.header,
                    position: idx
                })),
                rows: sortedRows.map((row, idx) => ({
                    id: row.id,
                    header: row.header,
                    position: idx,
                    rowPoints: row.rowPoints
                })),
                cells: rubric.cells
                    .map((cell) => ({
                        ...cell,
                        rowPosition: rowPositionMap.get(cell.rowId),
                        columnPosition: columnPositionMap.get(cell.columnId)
                    }))
                    .sort((a, b) => {
                        if (a.rowPosition !== b.rowPosition) {
                            return a.rowPosition - b.rowPosition;
                        }
                        return a.columnPosition - b.columnPosition;
                    })
            };
        });
    };

    const transformWordRubrics = (wordRubrics) => {
        return wordRubrics.map((rubric, rubricIndex) => {
            const columns = rubric.columns.map((column, columnIndex) => ({
                id: column.id || `word_rubric_${rubricIndex}_col_${columnIndex}`,
                header: column.header || `Column ${columnIndex + 1}`,
                position: columnIndex
            }));

            const rows = rubric.criteriaRows.map((row, rowIndex) => ({
                id: row.id || `word_rubric_${rubricIndex}_row_${rowIndex}`,
                header: row.header || `Criterion ${rowIndex + 1}`,
                position: rowIndex,
                rowPoints: row.rowPoints
            }));

            const cells = [];

            rows.forEach((row, rowIndex) => {
                columns.forEach((column, columnIndex) => {
                    const sourceColumn = rubric.columns[columnIndex];
                    const sourceRow = rubric.criteriaRows[rowIndex];

                    cells.push({
                        id: `${column.id}_${row.id}`,
                        rowId: row.id,
                        columnId: column.id,
                        rowPosition: row.position,
                        columnPosition: column.position,
                        points: {
                            value: sourceColumn?.points ?? 0
                        },
                        description: sourceRow?.descriptions?.[columnIndex] || ""
                    });
                });
            });

            return {
                __localId: `rubric_${rubricIndex}`,
                title: rubric.title || `Word Rubric ${rubricIndex + 1}`,
                rubricType: rubric.rubricType || "Numeric",
                columns,
                rows,
                cells
            };
        });
    };

    const transformTurnitinRubrics = (turnitinRubrics) => {
        return turnitinRubrics.map((rubric, rubricIndex) => {
            const sourceScales = asArray(rubric.scales);
            const sourceCriteria = asArray(rubric.criteria);
            const sourceCells = asArray(rubric.criterionScales);

            const sortedScales = [...sourceScales].sort((a, b) => {
                const aValue = Number(a?.value ?? a?.scale_value ?? 0);
                const bValue = Number(b?.value ?? b?.scale_value ?? 0);

                if (Number.isFinite(aValue) && Number.isFinite(bValue) && aValue !== bValue) {
                    return bValue - aValue;
                }

                return String(a?.name || "").localeCompare(String(b?.name || ""));
            });

            const columns = sortedScales.map((scale, columnIndex) => ({
                id: `turnitin_rubric_${rubricIndex}_col_${scale?.id ?? columnIndex}`,
                sourceScaleId: scale?.id ?? scale?.scale_id ?? columnIndex,
                header: cleanHtml(scale?.name || scale?.title || `Scale ${columnIndex + 1}`),
                position: columnIndex,
                points: Number(scale?.value ?? scale?.points ?? 0)
            }));

            const rows = sourceCriteria.map((criterion, rowIndex) => ({
                id: `turnitin_rubric_${rubricIndex}_row_${criterion?.id ?? rowIndex}`,
                sourceCriterionId: criterion?.id ?? criterion?.criterion_id ?? rowIndex,
                header:
                    cleanHtml(criterion?.name || criterion?.title || criterion?.description) ||
                    `Criterion ${rowIndex + 1}`,
                position: rowIndex,
                rowPoints: Number(criterion?.value ?? criterion?.points ?? columns[0]?.points ?? 0)
            }));

            const findCellDescription = (row, column) => {
                const matchesCriterion = (cell) =>
                    String(cell?.criterion ?? cell?.criterion_id ?? cell?.rubric_criterion_id ?? "") ===
                    String(row.sourceCriterionId);

                const matchesScale = (cell) => {
                    const rawScaleValue = cell?.scale_value ?? cell?.scale ?? cell?.scale_id ?? cell?.rubric_scale_id;
                    if (rawScaleValue === undefined || rawScaleValue === null) return false;

                    return (
                        String(rawScaleValue) === String(column.sourceScaleId) ||
                        String(rawScaleValue) === String(column.points)
                    );
                };

                const matchingCell = sourceCells.find(
                    (cell) => matchesCriterion(cell) && matchesScale(cell)
                );

                return cleanHtml(matchingCell?.description || matchingCell?.text || "");
            };

            const cells = [];

            rows.forEach((row) => {
                columns.forEach((column) => {
                    cells.push({
                        id: `${column.id}_${row.id}`,
                        rowId: row.id,
                        columnId: column.id,
                        rowPosition: row.position,
                        columnPosition: column.position,
                        points: { value: column.points },
                        description: findCellDescription(row, column)
                    });
                });
            });

            return {
                __localId: `rubric_${rubricIndex}`,
                title: rubric.title || `Turnitin Rubric ${rubricIndex + 1}`,
                rubricType: "Numeric",
                columns: columns.map(({ sourceScaleId, points, ...column }) => column),
                rows: rows.map(({ sourceCriterionId, ...row }) => row),
                cells
            };
        });
    };

    const transformRubricJson = () => {
        if (!rawJsonData) return;

        try {
            if (rawJsonData.sourceType === "canvas_outcomes") {
                if (!courseId) {
                    setError("Please enter a course ID before creating the Blackboard goals XML.");
                    return;
                }

                const xml = buildBlackboardGoalsXmlFromOutcomes(rawJsonData.outcomes, {
                    courseId,
                    goalSetTitle: goalSetTitle || `${courseId} Learning Outcomes`
                });

                const safeFileName = `${sanitizeXmlId(courseId, "course")}-learning-outcomes-goals.xml`;

                setGoalXmlData({
                    title: goalSetTitle || `${courseId} Learning Outcomes`,
                    outcomes: rawJsonData.outcomes,
                    xml
                });
                setGoalDownloadFileName(safeFileName);
                setJsonData([]);
                setSelectedRubricIds([]);
                setRawJsonData(null);
                setMessage(
                    `Transform complete. Created a Blackboard goals XML file with ${rawJsonData.outcomes.length} learning outcome(s).`
                );
                setError("");
                return;
            }

            const rubrics = asArray(rawJsonData.rubrics);

            const transformedRubrics =
                rawJsonData.sourceType === "canvas"
                    ? transformCanvasRubrics(rubrics)
                    : rawJsonData.sourceType === "csv"
                        ? transformCsvRubrics(rubrics)
                        : rawJsonData.sourceType === "word"
                            ? transformWordRubrics(rubrics)
                            : rawJsonData.sourceType === "turnitin"
                                ? transformTurnitinRubrics(rubrics)
                                : transformD2LRubrics(rubrics);

            setJsonData(transformedRubrics);
            setSelectedRubricIds(transformedRubrics.map((rubric) => rubric.__localId));
            setRawJsonData(null);
            setMessage(
                `Transform complete. Created ${transformedRubrics.length} Blackboard rubric payload(s) from ${sourceType.toUpperCase()}.`
            );
            setError("");
        } catch (err) {
            console.error("Error transforming rubric JSON:", err);
            setError("Error transforming rubric JSON: " + err.message);
        }
    };

    const toggleRubricSelection = (rubricId) => {
        setSelectedRubricIds((prev) =>
            prev.includes(rubricId)
                ? prev.filter((id) => id !== rubricId)
                : [...prev, rubricId]
        );
    };

    const handleSelectAllRubrics = () => {
        setSelectedRubricIds(jsonData.map((rubric) => rubric.__localId));
    };

    const handleClearRubricSelection = () => {
        setSelectedRubricIds([]);
    };

    const handleSendToBlackboard = async () => {
        if (!jsonData.length) {
            setError("No transformed data to send. Please transform first.");
            return;
        }

        const rubricsToSend = jsonData.filter((rubric) =>
            selectedRubricIds.includes(rubric.__localId)
        );

        if (!rubricsToSend.length) {
            setError("Please select at least one rubric to send.");
            return;
        }

        if (!courseId) {
            setError("Please enter a course ID.");
            return;
        }

        setLoading(true);
        setMessage("");
        setError("");
        setUploadResults([]);

        try {
            const results = [];

            for (const rubric of rubricsToSend) {
                const { __localId, ...payload } = rubric;

                try {
                    await axios.post(
                        `${platformUrl}/learn/api/public/v1/courses/courseId:${courseId}/rubrics`,
                        payload,
                        {
                            headers: {
                                Authorization: `Bearer ${token}`,
                                "Content-Type": "application/json"
                            }
                        }
                    );

                    results.push({
                        rubric: rubric.title,
                        status: "Success"
                    });
                } catch (err) {
                    results.push({
                        rubric: rubric.title,
                        status: "Failed",
                        error: err.response?.data
                            ? JSON.stringify(err.response.data)
                            : err.message
                    });
                }
            }

            setUploadResults(results);
            setMessage(`Upload process completed for ${rubricsToSend.length} rubric(s).`);
        } catch (err) {
            setError("Error sending rubrics to Blackboard.");
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadGoalsXml = () => {
        if (!goalXmlData?.xml) {
            setError("No goals XML has been created yet. Load and transform a learning outcomes file first.");
            return;
        }

        downloadTextFile(goalXmlData.xml, goalDownloadFileName, "application/xml;charset=utf-8;");
    };

    const csvEscape = (value) => {
        const str = String(value ?? "");
        if (str.includes(",") || str.includes('"') || str.includes("\n")) {
            return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
    };

    const handleDownloadCsvTemplate = () => {
        const templateRows = [
            [
                "rubric_title",
                "rubric_type",
                "row_header",
                "row_points",
                "row_position",
                "column_header",
                "column_points",
                "column_position",
                "cell_description"
            ],
            [
                "Writing Rubric",
                "Numeric",
                "Thesis",
                "4",
                "0",
                "Excellent",
                "4",
                "0",
                "Clear, focused, and well supported thesis."
            ],
            [
                "Writing Rubric",
                "Numeric",
                "Thesis",
                "4",
                "0",
                "Proficient",
                "3",
                "1",
                "Thesis is clear but could use more precision."
            ],
            [
                "Writing Rubric",
                "Numeric",
                "Thesis",
                "4",
                "0",
                "Developing",
                "2",
                "2",
                "Thesis is present but vague or uneven."
            ],
            [
                "Writing Rubric",
                "Numeric",
                "Organization",
                "4",
                "1",
                "Excellent",
                "4",
                "0",
                "Ideas are logically organized and easy to follow."
            ],
            [
                "Writing Rubric",
                "Numeric",
                "Organization",
                "4",
                "1",
                "Proficient",
                "3",
                "1",
                "Organization is generally clear with minor issues."
            ],
            [
                "Writing Rubric",
                "Numeric",
                "Organization",
                "4",
                "1",
                "Developing",
                "2",
                "2",
                "Organization is inconsistent or hard to follow."
            ]
        ];

        const csvContent = templateRows
            .map((row) => row.map(csvEscape).join(","))
            .join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "blackboard-rubric-template.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const fileAccept =
        sourceType === "csv"
            ? ".csv,text/csv"
            : sourceType === "word"
                ? ".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                : sourceType === "turnitin"
                    ? ".rbc,.json,application/json"
                    : ".xml,text/xml";

    return (
        <div className="upload-rubric-container">
            <p>
                To use this tool, export the rubrics you need from D2L, Canvas, or Turnitin, populate the
                CSV template, upload a Word document rubric formatted as a table, or upload a
                Canvas learning outcomes XML file. Choose the source type, load the file here,
                transform it, and either send selected rubrics to Blackboard or download the
                generated Blackboard goals XML file for import. When you load the file, you will
                first see the data converted into JSON. When you transform it, you will see the
                data transformed to the structure required for Blackboard.
            </p>

            <div style={{ marginBottom: 12 }}>
                <label htmlFor="sourceType" style={{ marginRight: 8 }}>
                    Source Type:
                </label>
                <select
                    id="sourceType"
                    value={sourceType}
                    onChange={(e) => {
                        setSourceType(e.target.value);
                        setRawJsonData(null);
                        setJsonData([]);
                        setSelectedRubricIds([]);
                        setMessage("");
                        setError("");
                        setUploadResults([]);
                        setGoalXmlData(null);
                    }}
                >
                    <option value="d2l">D2L</option>
                    <option value="canvas">Canvas</option>
                    <option value="turnitin">Turnitin RBC</option>
                    <option value="csv">CSV</option>
                    <option value="word">Word Document</option>
                    <option value="canvas_outcomes">Canvas Learning Outcomes</option>
                </select>

                {sourceType === "csv" && (
                    <button
                        type="button"
                        onClick={handleDownloadCsvTemplate}
                        style={{ marginLeft: 12 }}
                    >
                        Download CSV Template
                    </button>
                )}
            </div>

            {sourceType === "csv" && (
                <div style={{ marginBottom: 12, fontSize: 13 }}>
                    CSV columns required:
                    <code style={{ marginLeft: 6 }}>
                        rubric_title, rubric_type, row_header, row_points, row_position, column_header, column_points, column_position, cell_description
                    </code>
                </div>
            )}

            {sourceType === "word" && (
                <div style={{ marginBottom: 12, fontSize: 13 }}>
                    Word format expected:
                    <code style={{ marginLeft: 6 }}>
                        First row = Criteria + rating columns. Following rows = criteria and cell descriptions.
                    </code>
                </div>
            )}

            {sourceType === "canvas_outcomes" && (
                <div style={{ marginBottom: 12, fontSize: 13 }}>
                    Canvas learning outcome XML expected:
                    <code style={{ marginLeft: 6 }}>
                        &lt;learningOutcomes&gt;&lt;learningOutcome identifier=&quot;...&quot;&gt;...
                    </code>
                </div>
            )}

            {sourceType === "turnitin" && (
                <div style={{ marginBottom: 12, fontSize: 13 }}>
                    Turnitin RBC format expected:
                    <code style={{ marginLeft: 6 }}>
                        JSON with Rubric, RubricScale, RubricCriterion, and RubricCriterionScale.
                    </code>
                </div>
            )}

            <input type="file" accept={fileAccept} onChange={handleFileUpload} />

            <button onClick={transformRubricJson} disabled={!rawJsonData}>
                {isLearningOutcomeSource ? "Create Goals XML" : "Transform"}
            </button>

            <input
                type="text"
                placeholder="Enter Course ID"
                value={courseId}
                onChange={(e) => setCourseId(e.target.value)}
            />

            {isLearningOutcomeSource && (
                <input
                    type="text"
                    placeholder="Goal Set Name (optional)"
                    value={goalSetTitle}
                    onChange={(e) => setGoalSetTitle(e.target.value)}
                    style={{ marginLeft: 8 }}
                />
            )}

            {!isLearningOutcomeSource && (
                <button onClick={handleSendToBlackboard} disabled={loading}>
                    {loading ? "Sending..." : "Send Selected to Blackboard"}
                </button>
            )}

            {goalXmlData?.xml && (
                <button type="button" onClick={handleDownloadGoalsXml} style={{ marginLeft: 8 }}>
                    Download Blackboard Goals XML
                </button>
            )}

            {message && <div className="success-message">{message}</div>}
            {error && <div className="error-message">{error}</div>}

            {rawJsonData && (
                <div className="raw-json">
                    <h2>{rawJsonData.sourceType === "canvas_outcomes" ? "Raw Learning Outcomes JSON" : "Raw JSON Data"}</h2>
                    <pre className="json-preview">{JSON.stringify(rawJsonData, null, 2)}</pre>
                </div>
            )}

            {jsonData.length > 0 && (
                <div
                    className="rubric-selection"
                    style={{
                        marginTop: 16,
                        marginBottom: 16,
                        padding: 12,
                        border: "1px solid #ddd",
                        borderRadius: 6
                    }}
                >
                    <h2>Select Rubrics to Send</h2>

                    <div style={{ marginBottom: 10 }}>
                        <button type="button" onClick={handleSelectAllRubrics} style={{ marginRight: 8 }}>
                            Select All
                        </button>
                        <button type="button" onClick={handleClearRubricSelection}>
                            Clear All
                        </button>
                    </div>

                    {jsonData.map((rubric, index) => (
                        <div key={rubric.__localId} style={{ marginBottom: 6 }}>
                            <label>
                                <input
                                    type="checkbox"
                                    checked={selectedRubricIds.includes(rubric.__localId)}
                                    onChange={() => toggleRubricSelection(rubric.__localId)}
                                    style={{ marginRight: 8 }}
                                />
                                {index + 1}. {rubric.title}
                            </label>
                        </div>
                    ))}
                </div>
            )}

            {uploadResults.length > 0 && (
                <div className="upload-results">
                    <h2>Upload Results</h2>
                    <ul>
                        {uploadResults.map((result, index) => (
                            <li key={index}>
                                <strong>{result.rubric}</strong>: {result.status}
                                {result.status === "Failed" && ` - ${result.error}`}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {goalXmlData?.xml && (
                <div className="transformed-json">
                    <h2>Generated Blackboard Goals XML</h2>
                    <p style={{ fontSize: 13 }}>
                        Download this XML file, add it to a ZIP package, and import it from Blackboard
                        Administrator Panel &gt; Tools and Utilities &gt; Goals &gt; Add Goal Set &gt; Import Goal Set.
                    </p>
                    <pre className="json-preview">{goalXmlData.xml}</pre>
                </div>
            )}

            {jsonData.length > 0 && (
                <div className="transformed-json">
                    <h2>Transformed Blackboard JSON</h2>
                    {jsonData.map((rubric, index) => (
                        <div key={rubric.__localId} style={{ marginBottom: 20 }}>
                            <h3>
                                {index + 1}. {rubric.title}
                                {selectedRubricIds.includes(rubric.__localId) ? " (Selected)" : " (Not Selected)"}
                            </h3>
                            <pre className="json-preview">
                                {JSON.stringify(
                                    (({ __localId, ...rest }) => rest)(rubric),
                                    null,
                                    2
                                )}
                            </pre>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default UploadRubric;